import { SCol, SRow, SContainer } from "@avalara/skylab-sdk/react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useRoutes } from "react-router-dom";
import { selectColSpan, setColSpan } from "../../app/retailSlice";
import { selectSession, selectSkylabHeader } from "../../app/sessionSlice";
import featureFlag from "../../featureToggler/featureFlag";
import { useIsRetailOnlyUser } from "../../hooks/useAccessControl";
import useDeepCompareEffect from "../../hooks/useDeepCompareEffect";
import { useIsAuthorized } from "../../hooks/useIsAuthorized";
import adobeAnalytics from "../../shared/adobeAnalytics";
import { urlToPageMap } from "../../shared/Utils";
import "./navigation.css";
import { NavigationVisibilityContext } from "./NavigationVisibilityProvider";
import { allRoutes, retailOnlyRoutes } from "./routes";
import Subscription from "./Subscription";

const fullScreenWhenThisURL = [
    `${urlToPageMap.retail}/${urlToPageMap.retailCustomerAndCertificates.replace("/:id", "")}`,
    `${urlToPageMap.retail}/${urlToPageMap.retailSearch}`,
];
const fullScreenURLRegex = new RegExp(`^(${fullScreenWhenThisURL.join("|")})(?:/\\d+)?/?$`);
const matchFullScreenURL = url => fullScreenURLRegex.test(url);

function Navigation({ isSideNavEnabled }) {
    const dispatch = useDispatch();
    const { activeCompany, subscriptions, userId, accountId, isAuthenticated } = useSelector(
        selectSession,
        shallowEqual
    );
    const skylabHeader = useSelector(selectSkylabHeader, shallowEqual);
    const location = useLocation();
    const { hideHeader } = useContext(NavigationVisibilityContext);
    const colSpan = useSelector(selectColSpan);
    const [topSub, setTopSub] = useState("Exemption Certificates");
    const ishealthCheckGoingOn = location.pathname.includes("health-check");
    const isRetailView = location.pathname.startsWith(`${urlToPageMap.retail}`);
    const isRetailOnly = useIsRetailOnlyUser();
    const routes = useRoutes(isRetailOnly ? retailOnlyRoutes : allRoutes);

    const isPremiumTopSub = useIsAuthorized(featureFlag.topNav.premiumCategory, true);
    const isProTopSub = useIsAuthorized(featureFlag.topNav.proCategory, true);
    const isEssentialsTopSub = useIsAuthorized(featureFlag.topNav.essentialsCategory, true);

    const showSub = useCallback(() => {
        if (isPremiumTopSub) setTopSub(Subscription.ECMPremium);
        else if (isProTopSub) setTopSub(Subscription.ECMPro);
        else if (isEssentialsTopSub) setTopSub(Subscription.ECMEssentials);
    }, [isPremiumTopSub, isProTopSub, isEssentialsTopSub]);

    useDeepCompareEffect(() => {
        adobeAnalytics(location, subscriptions, userId, accountId, isAuthenticated);
        if (subscriptions.length >= 1) {
            showSub();
        }
    }, [location, subscriptions, showSub, userId, accountId, isAuthenticated]);

    useEffect(() => {
        if (isRetailView) {
            const modifiedLocation = location.pathname.replace(/\/$/, "");
            const shouldSetFullScreen = matchFullScreenURL(modifiedLocation);

            if (shouldSetFullScreen) {
                dispatch(setColSpan("md-10 12"));
            }
        }
        return () => {
            if (isRetailView) {
                // reset before exiting page
                dispatch(setColSpan("md-10 lg-7 xl-7 12"));
            }
        };
    }, [location.pathname, isRetailView, matchFullScreenURL, dispatch]);

    return (
        <React.Fragment>
            <main>
                <SContainer>
                    <SRow>
                        <SCol span="12">
                            {!ishealthCheckGoingOn && !isSideNavEnabled ? (
                                <SRow>
                                    <SCol
                                        span={isRetailView ? colSpan : ""}
                                        center={isRetailView ? "md" : ""}>
                                        <h4
                                            className="margin-top-none margin-bottom-md"
                                            hidden={hideHeader}>
                                            <span className="gray-darker workspace-letter-spacing margin-right-sm">
                                                {topSub}
                                            </span>
                                            <span className="divider-right margin-right-sm" />
                                            <span className="gray-dark margin-right-lg workspace-letter-spacing text-uppercase">
                                                {activeCompany.name}
                                            </span>
                                            <a href={skylabHeader.switchCompanyUrl}>
                                                Switch company
                                            </a>
                                        </h4>
                                    </SCol>
                                </SRow>
                            ) : null}
                            <div className="flex dl-flex-fill-height">{routes}</div>
                        </SCol>
                    </SRow>
                </SContainer>
            </main>
        </React.Fragment>
    );
}

export default React.memo(Navigation);
